import * as React from 'react'

import { BorderStyle, useToggle } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  BreakConfigurationFragment,
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { dateToString, stringToDate } from 'modules/core/lib/dates'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useBlockAutoScroll } from 'modules/attendance/lib/blockAutoScrollContext'
import {
  isFutureDate,
  setBackground,
  shouldExpandDayRow,
  styleDayRowBorder,
} from 'modules/attendance/lib/dayRowStyle'
import { useOvertimeRequests } from 'modules/attendance/lib/overtimeRequestsContext'
import { useTimeTrackingFeatureAvailabilityForCurrentCompany } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import AttendanceOvertimeUtils from 'modules/attendance/lib/utils/OvertimeRequest'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

import ShiftRowAccordion from './ShiftRowAccordion'
import { type GroupedByDateWorkedBreaks } from './types'

export interface DayRowProps {
  date: moment.Moment
  store: Store
  calendarDay: CalendarDay
  showHourBalance: boolean
  dayBreaks: AttendanceBreakConfigurations[]
  allBreaks: BreakConfigurationFragment[]
  workedBreaks: GroupedByDateWorkedBreaks
  shiftLocations?: AttendanceShiftLocationNodes
  expandAll: boolean
  inconsistencies?: Inconsistencies
  showStartsNextDayCheckbox: boolean
  balanceMinutes: number
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  workedMinutes: number
  effectiveWorkedMinutes: number
  workedMinutesNotApproved: number
  breaksMinutesRounded: number
  hasCorrectiveBreaks: boolean
  hasExtraHourManagementV2: boolean
  showExtraHoursColumn: boolean
  showComplementaryHoursColumn: boolean
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  shifts: TimesheetPageShiftFragment[]
}

const DayRow = (props: DayRowProps) => {
  const {
    date,
    store,
    calendarDay,
    showHourBalance,
    dayBreaks,
    allBreaks,
    workedBreaks,
    shiftLocations,
    expandAll,
    inconsistencies,
    showStartsNextDayCheckbox,
    balanceMinutes,
    attendanceSetting,
    estimatedTime,
    trackedMinutes,
    workedMinutes,
    effectiveWorkedMinutes,
    workedMinutesNotApproved,
    breaksMinutesRounded,
    hasCorrectiveBreaks,
    hasExtraHourManagementV2,
    showExtraHoursColumn,
    showComplementaryHoursColumn,
    editTimesheetRequests,
    shifts,
  } = props
  const { blockAutoScroll } = useBlockAutoScroll()
  const { date: calendarDate, is_laborable, is_leave } = calendarDay.attributes
  const dayDate = stringToDate(calendarDate, 'backend')
  const today = moment()
  const isBeforeToday = dayDate.isBefore(today, 'day')
  const isToday = dayDate.isSame(today, 'day')
  const dateKey = dateToString(date, 'backend')
  const [open, toggle] = useToggle(!!expandAll)

  const currentDay = date.date()

  const hasFutureDate = isFutureDate(calendarDay)
  const rowClassName = isToday && !blockAutoScroll ? 'current-date-row' : ''

  const overtimeRequests = useOvertimeRequests()
  const [hasUnresolvedShift, setHasUnresolvedShift] = React.useState(false)
  const [hasPendingEditRequest, setHasPendingEditRequest] = React.useState(false)
  const editTimesheetAuthorizationEnabled = useTimeTrackingFeatureAvailabilityForCurrentCompany(
    'time_tracking_edit_timesheet_authorization'
  )

  const hasEditTimesheetAuthorization = !!(
    editTimesheetAuthorizationEnabled && attendanceSetting.editTimesheetAuthorization
  )
  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )
  const hasMissingClockOut = !!EmployeesSettingsUtils.missingClockOutEnabled(attendanceSetting)
  const overtimeRequestMinutes = AttendanceOvertimeUtils.requestedOvertimeMinutes(
    overtimeRequests,
    currentDay.toString()
  )
  const { forgotClockOutToleranceMinutes = 0 } = attendanceSetting
  const estimatedWorkTime = estimatedTime.minutes
  const workedTime = workedMinutes
  const expandDayRow = shouldExpandDayRow({
    isToday,
    is_leave,
    currentDay,
    workedTime,
    is_laborable,
    estimatedWorkTime,
    expandAll: !!expandAll,
    today: today.get('date'),
  })

  const loadData = async () => {
    setHasUnresolvedShift(shifts.some((shift) => checkShiftIsUnresolved(shift)))
    setHasPendingEditRequest(editTimesheetRequests.some((request) => request.approved === null))
  }

  const checkShiftIsUnresolved = (shift: TimesheetPageShiftFragment) => {
    if (!hasMissingClockOut) return false

    const exceedsClockOutThreshold = store.exceedsWithForgotClockOutDuration({
      duration: AttendanceShiftUtils.hourlyDuration(shift),
      clockOutToleranceMinutes: forgotClockOutToleranceMinutes,
      overtimeRequestMinutes,
      estimatedMinutes: estimatedTime.minutes,
      trackedMinutes,
    })

    return !!exceedsClockOutThreshold && AttendanceShiftUtils.isOpenShift(shift)
  }

  useEffect(() => {
    if (expandDayRow) {
      toggle(true)()
    }
    if (!hasFutureDate) {
      loadData()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (expandAll !== null && !hasFutureDate) {
      toggle(expandAll)()
    }
  }, [expandAll]) // eslint-disable-line react-hooks/exhaustive-deps

  const borderLeftStyle: BorderStyle = styleDayRowBorder({
    hasFutureDate,
    inconsistenciesTotal: inconsistencies?.total,
    hasEditTimesheetAuthorization,
    hasTimeTrackingInconsistencies,
    hasUnresolvedShift,
    hasPendingEditRequest,
  })

  return (
    <ShiftRowAccordion
      store={store}
      open={open}
      date={date}
      toggle={toggle}
      dateKey={dateKey}
      dayBreaks={dayBreaks}
      allBreaks={allBreaks}
      className={rowClassName}
      calendarDay={calendarDay}
      workedBreaks={workedBreaks}
      isBeforeToday={isBeforeToday}
      setBackground={setBackground}
      hasFutureDate={hasFutureDate}
      shiftLocations={shiftLocations}
      showHourBalance={showHourBalance}
      attendanceSetting={attendanceSetting}
      hasUnresolvedShift={hasUnresolvedShift}
      overtimeRequestMinutes={overtimeRequestMinutes}
      borderLeftStyleDirection={{ left: borderLeftStyle }}
      hasEditTimesheetAuthorization={hasEditTimesheetAuthorization}
      editTimesheetRequests={editTimesheetRequests}
      inconsistencies={inconsistencies}
      showStartsNextDayCheckbox={showStartsNextDayCheckbox}
      balanceMinutes={balanceMinutes}
      estimatedTime={estimatedTime}
      trackedMinutes={trackedMinutes}
      workedMinutes={workedMinutes}
      effectiveWorkedMinutes={effectiveWorkedMinutes}
      workedMinutesNotApproved={workedMinutesNotApproved}
      breaksMinutesRounded={breaksMinutesRounded}
      hasCorrectiveBreaks={hasCorrectiveBreaks}
      hasExtraHourManagementV2={hasExtraHourManagementV2}
      showExtraHoursColumn={showExtraHoursColumn}
      showComplementaryHoursColumn={showComplementaryHoursColumn}
      shifts={shifts}
    />
  )
}

export default observer(DayRow)
